import { createContext, useContext, useState, useEffect, useCallback, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { CheckCircle2, XCircle, Sparkles, AlertTriangle, Loader2, X } from "lucide-react";
import "@/lib/toastSetup";
import { InstallPrompt } from "@/components/gull/InstallPrompt";

// Success/info toasts stay ~2.8s (see <Toaster duration>); errors and warnings stay longer so they can be read.
for (const kind of ["error", "warning"]) {
  const orig = toast[kind];
  toast[kind] = (msg, opts) => orig(msg, { duration: 4500, ...opts });
}
import { DrillProvider } from "@/components/gull/Drill";
import { LangProvider, useLang } from "@/i18n";
const BillingPage = lazy(() => import("@/pages/settings/Billing"));
const ChangePasswordPage = lazy(() => import("@/pages/settings/ChangePassword"));
const PaymentSuccessPage = lazy(() => import("@/pages/PaymentResult"));
const PaymentCancelPage = lazy(() => import("@/pages/PaymentResult").then((m) => ({ default: m.PaymentCancelPage })));

// Pages
const LandingPage = lazy(() => import("@/pages/Landing"));
const LoginPage = lazy(() => import("@/pages/Login"));
const CodeLoginPage = lazy(() => import("@/pages/CodeLogin"));
const WelcomePage = lazy(() => import("@/pages/Welcome"));
const TodayPage = lazy(() => import("@/pages/Today"));
const TodayBankPage = lazy(() => import("@/pages/TodayBank"));
const TodayBillPage = lazy(() => import("@/pages/TodayBill"));
const SettingsHubPage = lazy(() => import("@/pages/settings/SettingsHub"));
const TermsPage = lazy(() => import("@/pages/legal/Terms"));
const PrivacyPage = lazy(() => import("@/pages/legal/Privacy"));
const SmsConsentPage = lazy(() => import("@/pages/legal/SmsConsent"));
const SnapshotsPage = lazy(() => import("@/pages/settings/Snapshots"));
const SlowRequestsPage = lazy(() => import("@/pages/settings/SlowRequests"));
const ForgotPasswordPage = lazy(() => import("@/pages/ForgotPassword"));
const RegisterPage = lazy(() => import("@/pages/Register"));
const StartShiftPage = lazy(() => import("@/pages/daily/StartShift"));
const InventoryPage = lazy(() => import("@/pages/daily/Inventory"));
const ScanTicketsPage = lazy(() => import("@/pages/daily/ScanTickets"));
const ActivationPage = lazy(() => import("@/pages/daily/Activation"));
const ConfirmSoldPage = lazy(() => import("@/pages/daily/ConfirmSold"));
const LottoReportPage = lazy(() => import("@/pages/daily/LottoReport"));
const ReturnedBooksPage = lazy(() => import("@/pages/daily/ReturnedBooks"));
const BusinessReportPage = lazy(() => import("@/pages/daily/BusinessReport"));
const ShiftSnapshotPage = lazy(() => import("@/pages/daily/Snapshot"));
const CustomersPage = lazy(() => import("@/pages/business/SimpleLists").then((m) => ({ default: m.CustomersPage })));
const NewStorePage = lazy(() => import("@/pages/settings/NewStore"));
const BooksPage = lazy(() => import("@/pages/lottery/LotteryPages").then((m) => ({ default: m.BooksPage })));
const GameInfoPage = lazy(() => import("@/pages/lottery/LotteryPages").then((m) => ({ default: m.GameInfoPage })));
const LottoSalesReportPage = lazy(() => import("@/pages/lottery/LotteryPages").then((m) => ({ default: m.LottoSalesReportPage })));
const LotteryOverviewPage = lazy(() => import("@/pages/lottery/LotteryHub"));
const OrderSheetPage = lazy(() => import("@/pages/lottery/OrderSheet"));
const VendingPage = lazy(() => import("@/pages/lottery/Vending"));
const AdminStoresPage = lazy(() => import("@/pages/admin/AdminStores"));
const AdminBillingPage = lazy(() => import("@/pages/admin/AdminBilling"));
const BookDetailPage = lazy(() => import("@/pages/lottery/BookDetail"));
const LegacyDashboardPage = lazy(() => import("@/pages/LegacyDashboard"));
const ChecksPage = lazy(() => import("@/pages/bank/Checks"));
const UnifiedReportsPage = lazy(() => import("@/pages/reports/ReportPages"));
const QbHomePage = lazy(() => import("@/pages/qb/QbHome"));
const QbBillsPage = lazy(() => import("@/pages/qb/QbBills"));
const QbVendorsPage = lazy(() => import("@/pages/qb/QbPages").then((m) => ({ default: m.QbVendorsPage })));
const QbRegisterPage = lazy(() => import("@/pages/qb/QbPages").then((m) => ({ default: m.QbRegisterPage })));
const QbCustomersPage = lazy(() => import("@/pages/qb/QbCustomers"));
const QbCustomerDetailPage = lazy(() => import("@/pages/qb/QbCustomerDetail"));
const QbInvoicesPage = lazy(() => import("@/pages/qb/QbInvoices"));
const InvitePage = lazy(() => import("@/pages/InvitePage"));
const QbVendorDetailPage = lazy(() => import("@/pages/qb/QbVendorDetail"));
const QbBankPage = lazy(() => import("@/pages/qb/QbBank"));
const QbBankRulesPage = lazy(() => import("@/pages/qb/QbBankRules"));
const QbStatementOnlyPage = lazy(() => import("@/pages/qb/QbStatementOnly"));
const QbCpaPage = lazy(() => import("@/pages/qb/QbCpa"));
const ReconcileListPage = lazy(() => import("@/pages/qb/Reconcile"));
const ReconcilePage = lazy(() => import("@/pages/qb/Reconcile").then((m) => ({ default: m.ReconcilePage })));
const PayrollReportsPage = lazy(() => import("@/pages/qb/PayrollReports"));
const DeliveryPage = lazy(() => import("@/pages/qb/Delivery"));
const RestaurantDayPage = lazy(() => import("@/pages/restaurant/RestaurantDay"));
const RestaurantDaysPage = lazy(() => import("@/pages/restaurant/RestaurantDay").then((m) => ({ default: m.RestaurantDaysPage })));
const PlatformsPage = lazy(() => import("@/pages/restaurant/Platforms"));
const QbAccountsPage = lazy(() => import("@/pages/qb/QbAccounts"));
const QbReportsPage = lazy(() => import("@/pages/qb/QbReports"));
const PayrollPage = lazy(() => import("@/pages/qb/Payroll"));
const HistoryImportPage = lazy(() => import("@/pages/qb/HistoryImport"));
const ReclassifyPage = lazy(() => import("@/pages/qb/Reclassify"));
const QbDocumentsPage = lazy(() => import("@/pages/qb/QbDocuments"));
const SetPasswordPage = lazy(() => import("@/pages/SetPassword"));
const DocumentVaultPage = lazy(() => import("@/pages/DocumentVault"));
const PublicUploadPage = lazy(() => import("@/pages/DocumentVault").then((m) => ({ default: m.PublicUploadPage })));
const StatementFolderPage = lazy(() => import("@/pages/StatementFolder"));
const FirmPage = lazy(() => import("@/pages/Firm"));
const ChooseCompanyPage = lazy(() => import("@/pages/ChooseCompany"));
const StoreSettingsPage = lazy(() => import("@/pages/settings/SettingsPages").then((m) => ({ default: m.StoreSettingsPage })));
const UsersPage = lazy(() => import("@/pages/settings/SettingsPages").then((m) => ({ default: m.UsersPage })));
import { AppDialogs } from "@/components/gull/dialogs";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Store Context
const StoreContext = createContext(null);

export const useStore = () => {
  const context = useContext(StoreContext);
  if (!context) {
    throw new Error("useStore must be used within StoreProvider");
  }
  return context;
};

// Auth Provider Component
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);

  const login = useCallback(async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const { user: userData, token: authToken } = response.data;
      
      localStorage.setItem("token", authToken);
      setToken(authToken);
      setUser(userData);
      toast.success("Welcome back!");
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || "Login failed";
      toast.error(message);
      return { success: false, error: message };
    }
  }, []);

  const register = useCallback(async (userData) => {
    try {
      const response = await axios.post(`${API}/auth/register`, userData);
      const { user: newUser, token: authToken } = response.data;
      
      localStorage.setItem("token", authToken);
      setToken(authToken);
      setUser(newUser);
      toast.success("Account created successfully!");
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || "Registration failed";
      toast.error(message);
      return { success: false, error: message };
    }
  }, []);

  const setSession = useCallback((userData, authToken) => {
    localStorage.setItem("token", authToken);
    setToken(authToken);
    setUser(userData);
  }, []);

  const refreshUser = useCallback(async () => {
    const tk = localStorage.getItem("token");
    if (!tk) return;
    try { const r = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${tk}` } }); setUser(r.data); } catch { /* keep current */ }
  }, []);

  const setPrefs = useCallback(async (prefs) => {
    const tk = localStorage.getItem("token");
    const r = await axios.put(`${API}/auth/prefs`, prefs, { headers: { Authorization: `Bearer ${tk}` } });
    setUser((u) => ({ ...u, ...r.data }));
    return r.data;
  }, []);

  const logout = useCallback(async () => {
    try {
      if (token) {
        await axios.post(`${API}/auth/logout`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      localStorage.removeItem("token");
      localStorage.removeItem("selectedStore");
      setToken(null);
      setUser(null);
      toast.success("Logged out");
    }
  }, [token]);

  useEffect(() => {
    const checkAuth = async () => {
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUser(response.data);
      } catch (error) {
        console.error("Auth check failed:", error);
        if (error?.response?.status === 401 || error?.response?.status === 403) {
          localStorage.removeItem("token");
          setToken(null);
        } else {
          setUser((u) => u || { first_name: "", last_name: "", email: "" });
        }
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, [token]);

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, setSession, refreshUser, setPrefs }}>
      {children}
    </AuthContext.Provider>
  );
};

// Store Provider Component
const StoreProvider = ({ children }) => {
  const { token, user } = useAuth();
  const [stores, setStores] = useState([]);
  const [selectedStore, setSelectedStore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchedFor, setFetchedFor] = useState(null); // token the current `stores` belong to

  const fetchStores = useCallback(async () => {
    if (!token) return;
    
    try {
      const response = await axios.get(`${API}/stores`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStores(response.data);
      
      // Auto-select first store or stored preference
      const storedStoreId = localStorage.getItem("selectedStore");
      const storeToSelect = response.data.find(s => s.id === storedStoreId) || response.data.find(s => s.owner_id === user?.id) || response.data[0];
      if (storeToSelect) {
        setSelectedStore(storeToSelect);
        localStorage.setItem("selectedStore", storeToSelect.id);
      }
    } catch (error) {
      console.error("Failed to fetch stores:", error);
    } finally {
      setFetchedFor(token);
      setLoading(false);
    }
  }, [token]);

  const selectStore = useCallback((store) => {
    setSelectedStore(store);
    localStorage.setItem("selectedStore", store.id);
  }, []);

  const createStore = useCallback(async (storeData) => {
    try {
      const response = await axios.post(`${API}/stores`, storeData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const newStore = response.data;
      setStores(prev => [...prev, newStore]);
      setSelectedStore(newStore);
      localStorage.setItem("selectedStore", newStore.id);
      toast.success("Store created!");
      return { success: true, store: newStore };
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to create store";
      toast.error(message);
      return { success: false, error: message };
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchStores();
    } else {
      setStores([]);
      setSelectedStore(null);
      setLoading(false);
    }
  }, [token, fetchStores]);

  return (
    <StoreContext.Provider value={{ 
      stores, 
      selectedStore, 
      loading: loading || (!!token && fetchedFor !== token), 
      selectStore, 
      createStore,
      refreshStores: fetchStores 
    }}>
      {children}
    </StoreContext.Provider>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children, allowNoStore = false }) => {
  const { user, loading } = useAuth();
  const { stores, loading: storesLoading } = useStore();
  
  if (loading || (user && storesLoading && !allowNoStore)) {
    return (
      <div className="min-h-screen bg-[#f6f5fa] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#663399] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  if (user.needs_password && window.location.pathname !== "/set-password") {
    return <Navigate to="/set-password" replace />;
  }
  if (!allowNoStore && !storesLoading && stores.length === 0 && !user.platform_admin) {
    return <Navigate to="/welcome" replace />;
  }
  
  return children;
};

/* /dashboard: people in Simple mode land on the Today screen */
const HomeRoute = () => {
  const { user } = useAuth();
  if ((user?.ui_mode || "advanced") === "simple") return <Navigate to="/today" replace />;
  return <LegacyDashboardPage />;
};

/* Apply the language saved on the account once the user loads (doesn't overwrite a choice made on this device during this visit) */
const LangSync = () => {
  const { user } = useAuth();
  const { lang, setLang } = useLang();
  useEffect(() => { if (user?.lang && user.lang !== lang && !sessionStorage.getItem("tp_lang_touched")) setLang(user.lang, { save: false }); }, [user?.lang]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { const h = () => sessionStorage.setItem("tp_lang_touched", "1"); window.addEventListener("tp-lang-change", h); return () => window.removeEventListener("tp-lang-change", h); }, []);
  return null;
};

// Public Route (redirect to dashboard if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#f6f5fa] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#663399] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (user) {
    return <Navigate to="/choose-company" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Suspense fallback={<div className="flex h-screen items-center justify-center text-sm text-gray-400" data-testid="page-loading">Loading…</div>}>
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/sms-consent" element={<SmsConsentPage />} />
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/login/password" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/login/code" element={<PublicRoute><CodeLoginPage /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><CodeLoginPage signup /></PublicRoute>} />
      <Route path="/register/password" element={<PublicRoute><RegisterPage /></PublicRoute>} />
      <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />
      <Route path="/invite/:token" element={<InvitePage />} />
      <Route path="/upload/:token" element={<PublicUploadPage />} />
      <Route path="/documents" element={<ProtectedRoute><DocumentVaultPage /></ProtectedRoute>} />
      <Route path="/documents/statements/:accountId" element={<ProtectedRoute><StatementFolderPage /></ProtectedRoute>} />
      <Route path="/firm" element={<ProtectedRoute allowNoStore><FirmPage /></ProtectedRoute>} />
      
      {/* Protected Routes */}
      <Route path="/dashboard" element={<ProtectedRoute><HomeRoute /></ProtectedRoute>} />
      <Route path="/dashboard/full" element={<ProtectedRoute><LegacyDashboardPage /></ProtectedRoute>} />
      <Route path="/today" element={<ProtectedRoute><TodayPage /></ProtectedRoute>} />
      <Route path="/today/bank" element={<ProtectedRoute><TodayBankPage /></ProtectedRoute>} />
      <Route path="/today/bill" element={<ProtectedRoute><TodayBillPage /></ProtectedRoute>} />
      <Route path="/welcome" element={<ProtectedRoute allowNoStore><WelcomePage /></ProtectedRoute>} />
      <Route path="/set-password" element={<ProtectedRoute allowNoStore><SetPasswordPage /></ProtectedRoute>} />
      <Route path="/choose-company" element={<ProtectedRoute allowNoStore><ChooseCompanyPage /></ProtectedRoute>} />
      {/* Daily Reports flow (1:1 with the PHP pages) */}
      <Route path="/startshift" element={<ProtectedRoute><StartShiftPage /></ProtectedRoute>} />
      <Route path="/inventory" element={<ProtectedRoute><InventoryPage /></ProtectedRoute>} />
      <Route path="/sales-batch" element={<ProtectedRoute><ScanTicketsPage /></ProtectedRoute>} />
      <Route path="/activation" element={<ProtectedRoute><ActivationPage /></ProtectedRoute>} />
      <Route path="/ticket-sold" element={<ProtectedRoute><ConfirmSoldPage /></ProtectedRoute>} />
      <Route path="/book-returned" element={<ProtectedRoute><ReturnedBooksPage /></ProtectedRoute>} />
      <Route path="/view-reports" element={<ProtectedRoute><LottoReportPage /></ProtectedRoute>} />
      <Route path="/shift" element={<Navigate to="/startshift" replace />} />
      <Route path="/new-store" element={<ProtectedRoute allowNoStore><NewStorePage /></ProtectedRoute>} />
      <Route path="/lottery" element={<ProtectedRoute><LotteryOverviewPage /></ProtectedRoute>} />
      <Route path="/lottery/sales" element={<ProtectedRoute><LottoSalesReportPage /></ProtectedRoute>} />
      <Route path="/lottery/books" element={<ProtectedRoute><BooksPage /></ProtectedRoute>} />
      <Route path="/lottery/games" element={<ProtectedRoute><GameInfoPage /></ProtectedRoute>} />
      <Route path="/lottery/order-sheet" element={<ProtectedRoute><OrderSheetPage /></ProtectedRoute>} />
      <Route path="/lottery/vending" element={<ProtectedRoute><VendingPage /></ProtectedRoute>} />
      <Route path="/admin/stores" element={<ProtectedRoute allowNoStore><AdminStoresPage /></ProtectedRoute>} />
      <Route path="/admin/billing" element={<ProtectedRoute allowNoStore><AdminBillingPage /></ProtectedRoute>} />
      <Route path="/lotto-sales-report" element={<Navigate to="/lottery/sales" replace />} />
      <Route path="/books-in-stock" element={<Navigate to="/lottery/books?status=in_stock" replace />} />
      <Route path="/all-books-received" element={<Navigate to="/lottery/books?status=all" replace />} />
      <Route path="/active-inventory" element={<Navigate to="/lottery/books?status=active" replace />} />
      <Route path="/book-sold" element={<Navigate to="/lottery/books?status=sold" replace />} />
      <Route path="/game-info" element={<Navigate to="/lottery/games" replace />} />
      <Route path="/banking" element={<Navigate to="/business/register" replace />} />
      <Route path="/invoices" element={<Navigate to="/business/bills" replace />} />
      <Route path="/vendors" element={<Navigate to="/business/vendors" replace />} />
      <Route path="/payroll" element={<Navigate to="/business" replace />} />
      <Route path="/other-income" element={<Navigate to="/business" replace />} />
      <Route path="/checks" element={<ProtectedRoute><ChecksPage /></ProtectedRoute>} />
      <Route path="/reports" element={<ProtectedRoute><BusinessReportPage /></ProtectedRoute>} />
      <Route path="/shift-snapshot" element={<ProtectedRoute><ShiftSnapshotPage /></ProtectedRoute>} />
      <Route path="/all-reports" element={<ProtectedRoute><UnifiedReportsPage /></ProtectedRoute>} />
      <Route path="/lotto-book/:id" element={<ProtectedRoute><BookDetailPage /></ProtectedRoute>} />
      <Route path="/business" element={<ProtectedRoute><QbHomePage /></ProtectedRoute>} />
      <Route path="/business/bills" element={<ProtectedRoute><QbBillsPage /></ProtectedRoute>} />
      <Route path="/business/vendors" element={<ProtectedRoute><QbVendorsPage /></ProtectedRoute>} />
      <Route path="/business/customers" element={<ProtectedRoute><QbCustomersPage /></ProtectedRoute>} />
      <Route path="/business/invoices" element={<ProtectedRoute><QbInvoicesPage /></ProtectedRoute>} />
      <Route path="/business/vendors/:vid" element={<ProtectedRoute><QbVendorDetailPage /></ProtectedRoute>} />
      <Route path="/business/customers/:cid" element={<ProtectedRoute><QbCustomerDetailPage /></ProtectedRoute>} />
      <Route path="/business/bank" element={<ProtectedRoute><QbBankPage /></ProtectedRoute>} />
      <Route path="/business/cpa" element={<ProtectedRoute><QbCpaPage /></ProtectedRoute>} />
      <Route path="/business/reconcile" element={<ProtectedRoute><ReconcileListPage /></ProtectedRoute>} />
      <Route path="/business/reconcile/:docId" element={<ProtectedRoute><ReconcilePage /></ProtectedRoute>} />
      <Route path="/business/payroll/reports" element={<ProtectedRoute><PayrollReportsPage /></ProtectedRoute>} />
      <Route path="/business/delivery" element={<ProtectedRoute><DeliveryPage /></ProtectedRoute>} />
      <Route path="/business/bank/rules" element={<ProtectedRoute><QbBankRulesPage /></ProtectedRoute>} />
      <Route path="/business/bank/statement-only" element={<ProtectedRoute><QbStatementOnlyPage /></ProtectedRoute>} />
      <Route path="/restaurant" element={<ProtectedRoute><RestaurantDaysPage /></ProtectedRoute>} />
      <Route path="/restaurant/day/:day" element={<ProtectedRoute><RestaurantDayPage /></ProtectedRoute>} />
      <Route path="/restaurant/platforms" element={<ProtectedRoute><PlatformsPage /></ProtectedRoute>} />
      <Route path="/business/accounts" element={<ProtectedRoute><QbAccountsPage /></ProtectedRoute>} />
      <Route path="/business/reports" element={<ProtectedRoute><QbReportsPage /></ProtectedRoute>} />
      <Route path="/business/payroll" element={<ProtectedRoute><PayrollPage /></ProtectedRoute>} />
      <Route path="/business/history" element={<Navigate to="/business/quickbooks" replace />} />
      <Route path="/business/quickbooks" element={<ProtectedRoute><HistoryImportPage /></ProtectedRoute>} />
      <Route path="/business/reclassify" element={<ProtectedRoute><ReclassifyPage /></ProtectedRoute>} />
      <Route path="/business/qb-documents" element={<ProtectedRoute><QbDocumentsPage /></ProtectedRoute>} />
      <Route path="/business/register" element={<ProtectedRoute><QbRegisterPage /></ProtectedRoute>} />
      <Route path="/customers" element={<ProtectedRoute><CustomersPage /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><SettingsHubPage /></ProtectedRoute>} />
      <Route path="/settings/all" element={<ProtectedRoute><StoreSettingsPage /></ProtectedRoute>} />
      <Route path="/billing" element={<ProtectedRoute><BillingPage /></ProtectedRoute>} />
      <Route path="/change-password" element={<ProtectedRoute><ChangePasswordPage /></ProtectedRoute>} />
      <Route path="/payment/success" element={<ProtectedRoute><PaymentSuccessPage /></ProtectedRoute>} />
      <Route path="/payment/cancel" element={<ProtectedRoute><PaymentCancelPage /></ProtectedRoute>} />
      <Route path="/users" element={<ProtectedRoute><UsersPage /></ProtectedRoute>} />
      <Route path="/snapshots" element={<ProtectedRoute><SnapshotsPage /></ProtectedRoute>} />
      <Route path="/settings/speed" element={<ProtectedRoute><SlowRequestsPage /></ProtectedRoute>} />
      <Route path="/user-role-settings" element={<Navigate to="/users" replace />} />
      
      {/* Catch all */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <StoreProvider>
          <InstallPrompt />
          <AppDialogs />
          <Toaster
            position="top-right"
            duration={2800}
            visibleToasts={3}
            gap={10}
            offset={18}
            closeButton
            icons={{ success: <CheckCircle2 className="h-[18px] w-[18px]" />, error: <XCircle className="h-[18px] w-[18px]" />, info: <Sparkles className="h-[18px] w-[18px]" />, warning: <AlertTriangle className="h-[18px] w-[18px]" />, loading: <Loader2 className="h-[18px] w-[18px] animate-spin" />, close: <X className="h-3.5 w-3.5" /> }}
            toastOptions={{
              unstyled: true,
              classNames: { toast: "tp-toast", title: "tp-toast-title", description: "tp-toast-desc", icon: "tp-toast-icon", content: "tp-toast-content", success: "tp-toast-success", error: "tp-toast-error", info: "tp-toast-info", warning: "tp-toast-warning", loading: "tp-toast-info", default: "tp-toast-info", actionButton: "tp-toast-action", cancelButton: "tp-toast-cancel", closeButton: "tp-toast-close" },
              duration: 2800,
            }}
          />
          <LangProvider><LangSync /><DrillProvider><AppRoutes /></DrillProvider></LangProvider>
        </StoreProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
